import * as React from 'react'

import * as plume from '@ulule/owl-kit-components/next'

import { AuthenticatedUser } from '@owl-nest/api-client/latest'
import { EulaModal } from '@owl-nest/eula-modal'
import * as service from '@owl-nest/services'
import { useTracking, SITE, EVENT_TYPE, CATEGORY } from '@owl-nest/shadow'

import { ExistingUser } from './ExistingUser'
import { UnknownUser } from './UnknownUser'
import { NewUser } from './NewUser'
import { KnownUser } from './KnownUser'
import { Identity } from './Identity'

import * as S from './styles'

type ConnectProps<EXTRA_FIELDS extends string = ''> = {
  allowGuest?: boolean
  compactMode?: boolean
  context?: string
  extraFields?: EXTRA_FIELDS[]
  onGuestLoggedIn?: (user: AuthenticatedUser<EXTRA_FIELDS>) => void
  onLoggedIn?: (user: AuthenticatedUser<EXTRA_FIELDS>) => void
  onSignedUp?: (user: AuthenticatedUser) => void
  showHint?: boolean
  translations?: Translations
}

type Translations = {
  unknownGuestUser: {
    cta: string
  }
  existingUser: {
    cta: string
  }
}

export function Connect<EXTRA_FIELDS extends string = ''>({
  allowGuest,
  compactMode = false,
  context,
  extraFields = [],
  onGuestLoggedIn,
  onLoggedIn,
  onSignedUp,
  showHint = true,
  translations,
}: ConnectProps<EXTRA_FIELDS>): React.ReactElement<ConnectProps<EXTRA_FIELDS>> | null {
  const tracking = useTracking()
  const auth = service.user.useAuth({
    onSignedUp: (user) => {
      tracking.track({
        event: EVENT_TYPE.GENERIC,
        siteCategory: SITE.CONNECT,
        eventCategory: CATEGORY.REGISTRATION,
        eventAction: 'Done',
        eventLabel: 'Ulule',
        ululeUserId: `${user.id}`,
      })
      onSignedUp && onSignedUp(user)
    },
    onGuestLoggedIn,
    onLoggedIn,
    extraFields,
  })

  return (
    <>
      {auth.type === 'loggedin' && <EulaModal user={auth.user} refresh={auth.refresh} />}
      <S.Wrapper $compactMode={compactMode}>
        {auth.type === 'initial' ? (
          <plume.Spinner />
        ) : auth.type === 'unknown' ? (
          <UnknownUser
            auth={auth}
            allowGuest={allowGuest}
            showHint={showHint}
            translations={translations?.unknownGuestUser}
          />
        ) : auth.type === 'known' ? (
          <KnownUser auth={auth} />
        ) : auth.type === 'existing' ? (
          <ExistingUser auth={auth} showHint={showHint} translations={translations?.existingUser} />
        ) : auth.type === 'new' ? (
          <NewUser auth={auth} context={context} showHint={showHint} />
        ) : (
          auth.type === 'loggedin' && <Identity />
        )}
      </S.Wrapper>
    </>
  )
}
